import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarRange, DollarSign, Package, ReceiptText, RotateCcw, Sparkles, Tag } from 'lucide-react';
import { useApiClient } from '../lib/apiClient';
import { formatMoney } from '../i18n/orgFormat';

function formatCurrency(value: number) {
  return formatMoney(value, { maximumFractionDigits: 0 });
}

function formatNumber(value: number | null | undefined) {
  return Number(value || 0).toLocaleString();
}

function formatPercent(value: number) {
  if (!Number.isFinite(value)) return '0%';
  return `${Math.round(value)}%`;
}

function safeRate(numerator: number, denominator: number) {
  if (!denominator) return 0;
  return (numerator / denominator) * 100;
}

function toDateInputValue(date: Date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function startOfWeek(date: Date) {
  const value = new Date(date);
  const day = value.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  value.setDate(value.getDate() + diff);
  value.setHours(0, 0, 0, 0);
  return value;
}

function buildPresetRange(preset: 'week' | 'month') {
  const now = new Date();
  if (preset === 'week') {
    return { startDate: toDateInputValue(startOfWeek(now)), endDate: toDateInputValue(now) };
  }
  return { startDate: toDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1)), endDate: toDateInputValue(now) };
}

type FinancialReport = {
  range?: { start: string; end: string; locationIds: string[] };
  summary: {
    revenue: number;
    discounts: number;
    totals: number;
    invoiceCount: number;
    averageTicket: number;
    packageLiability: number;
    activePackages: number;
    campaignReturn?: number;
    campaigns?: number;
  };
  servicePerformance: Array<{ appointmentTypeId: string; serviceName: string; invoiceCount: number; revenue: number; discounts: number }>;
};

type PeriodPreset = 'week' | 'month' | 'custom';

export default function FinancialReportsPage() {
  const { fetchJson } = useApiClient();
  const [data, setData] = useState<FinancialReport | null>(null);
  const [scope, setScope] = useState<'default' | 'all'>('default');
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>('month');
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return toDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [endDate, setEndDate] = useState(() => toDateInputValue(new Date()));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ scope });
      if (startDate) params.set('start', new Date(startDate).toISOString());
      if (endDate) {
        const inclusiveEnd = new Date(endDate);
        inclusiveEnd.setDate(inclusiveEnd.getDate() + 1);
        params.set('end', inclusiveEnd.toISOString());
      }
      const payload = await fetchJson<FinancialReport>(`/reports/financial?${params.toString()}`);
      setData(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Financial reports require explicit permission.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [scope, startDate, endDate]);

  function applyPreset(preset: 'week' | 'month') {
    const range = buildPresetRange(preset);
    setPeriodPreset(preset);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  }

  const summary = data?.summary;
  const rangeLabel = data?.range
    ? `${new Date(data.range.start).toLocaleDateString()} to ${new Date(data.range.end).toLocaleDateString()}`
    : `${startDate} to ${endDate}`;

  const rankedServices = useMemo(() => (data?.servicePerformance ?? []).map((row) => ({
    ...row,
    discountRate: safeRate(row.discounts, row.revenue + row.discounts),
    averageRevenuePerInvoice: row.invoiceCount ? row.revenue / row.invoiceCount : 0,
  })), [data?.servicePerformance]);

  const insights = useMemo(() => {
    if (!summary) return [];
    const discountRate = safeRate(summary.discounts, summary.totals || summary.revenue + summary.discounts);
    const rows: Array<{ title: string; detail: string; tone: 'good' | 'watch' | 'neutral' }> = [];
    if (discountRate >= 12) {
      rows.push({
        title: 'Discounting is elevated',
        detail: `${formatPercent(discountRate)} of billed value was discounted in this range.`,
        tone: 'watch',
      });
    }
    if ((summary.activePackages || 0) > 0 || (summary.packageLiability || 0) > 0) {
      rows.push({
        title: 'Prepaid liability to monitor',
        detail: `${formatNumber(summary.activePackages)} active package${summary.activePackages === 1 ? '' : 's'} with ${formatNumber(summary.packageLiability)} remaining units.`,
        tone: 'neutral',
      });
    }
    if ((summary.campaigns || 0) > 0) {
      rows.push({
        title: 'Campaign redemptions tracked',
        detail: `${formatNumber(summary.campaignReturn)} redemption${summary.campaignReturn === 1 ? '' : 's'} across ${formatNumber(summary.campaigns)} campaign${summary.campaigns === 1 ? '' : 's'}.`,
        tone: 'good',
      });
    }
    if (!rows.length) {
      rows.push({
        title: 'Financial picture looks clean',
        detail: 'Revenue, discounting, and package balances are within a normal operating range for this filter.',
        tone: 'good',
      });
    }
    return rows.slice(0, 3);
  }, [summary]);

  return (
    <div className="min-h-full bg-slate-50/60 px-5 py-6 lg:px-8">
      <main className="mx-auto max-w-[1500px] space-y-5">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-3xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Financial reports</p>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">Revenue and performance</h1>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                Review billed revenue, discount pressure, invoice activity, and package exposure. Use operations for visit flow and service volume.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Link to="/reports/operations" className="inline-flex h-10 items-center rounded-xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50">
                Back to operations
              </Link>
              <select value={scope} onChange={(event) => setScope(event.target.value as typeof scope)} className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 shadow-sm">
                <option value="default">Only my assigned locations</option>
                <option value="all">All accessible locations</option>
              </select>
              <button type="button" onClick={() => void load()} className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50">
                <RotateCcw size={16} />
                Refresh
              </button>
            </div>
          </div>

          <div className="mt-5 grid gap-3 lg:grid-cols-[auto_1fr] lg:items-center">
            <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
              <button
                type="button"
                onClick={() => applyPreset('week')}
                className={`rounded-lg px-3 py-2 text-sm font-medium transition ${periodPreset === 'week' ? 'bg-slate-950 text-white shadow-sm' : 'text-slate-600'}`}
              >
                This week
              </button>
              <button
                type="button"
                onClick={() => applyPreset('month')}
                className={`rounded-lg px-3 py-2 text-sm font-medium transition ${periodPreset === 'month' ? 'bg-slate-950 text-white shadow-sm' : 'text-slate-600'}`}
              >
                This month
              </button>
            </div>
            <div className="grid gap-3 md:grid-cols-[minmax(0,180px)_minmax(0,180px)] xl:max-w-[420px]">
              <label className="flex h-10 items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 shadow-sm">
                <CalendarRange size={15} className="text-slate-400" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(event) => {
                    setPeriodPreset('custom');
                    setStartDate(event.target.value);
                  }}
                  className="w-full bg-transparent outline-none"
                  aria-label="Start date"
                />
              </label>
              <label className="flex h-10 items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 shadow-sm">
                <CalendarRange size={15} className="text-slate-400" />
                <input
                  type="date"
                  value={endDate}
                  onChange={(event) => {
                    setPeriodPreset('custom');
                    setEndDate(event.target.value);
                  }}
                  className="w-full bg-transparent outline-none"
                  aria-label="End date"
                />
              </label>
            </div>
          </div>
        </section>

        {error ? <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{error}</div> : null}
        {loading ? <div className="rounded-2xl border border-slate-200 bg-white p-5 text-slate-500 shadow-sm">Loading financial report...</div> : null}

        {summary ? (
          <>
            <section className="grid gap-4 xl:grid-cols-[1fr_340px]">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-950">Financial snapshot</p>
                    <p className="mt-1 text-sm text-slate-500">Showing billing data from {rangeLabel}.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
                      {scope === 'all' ? 'All accessible locations' : 'Only my assigned locations'}
                    </span>
                  </div>
                </div>
                <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  <MetricCard icon={<DollarSign size={18} />} label="Revenue" value={formatCurrency(summary.revenue)} detail={`${formatNumber(summary.invoiceCount)} invoices`} tone="emerald" />
                  <MetricCard icon={<Tag size={18} />} label="Discounts" value={formatCurrency(summary.discounts)} detail={`${formatPercent(safeRate(summary.discounts, summary.totals || summary.revenue + summary.discounts))} of billed value`} tone="amber" />
                  <MetricCard icon={<ReceiptText size={18} />} label="Total billed" value={formatCurrency(summary.totals)} detail="Subtotal before discounts" tone="slate" />
                  <MetricCard icon={<ReceiptText size={18} />} label="Average invoice" value={formatCurrency(summary.averageTicket)} detail="Average collected per invoice" tone="blue" />
                  <MetricCard icon={<Package size={18} />} label="Active packages" value={formatNumber(summary.activePackages)} detail={`${formatNumber(summary.packageLiability)} units remaining`} tone="violet" />
                  <MetricCard icon={<Sparkles size={18} />} label="Campaign redemptions" value={formatNumber(summary.campaignReturn)} detail={`${formatNumber(summary.campaigns)} active campaigns in range`} tone="slate" />
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-sm font-semibold text-slate-950">What to watch</h2>
                <div className="mt-4 space-y-3">
                  {insights.map((insight) => (
                    <InsightRow key={insight.title} title={insight.title} detail={insight.detail} tone={insight.tone} />
                  ))}
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
                <div>
                  <h2 className="text-sm font-semibold text-slate-950">Service performance</h2>
                  <p className="mt-1 text-sm text-slate-500">Ranked by revenue so billing impact is visible immediately.</p>
                </div>
                <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
                  Visible revenue: {formatCurrency(rankedServices.reduce((sum, row) => sum + row.revenue, 0))}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-5 py-3">Service</th>
                      <th className="px-5 py-3">Invoices</th>
                      <th className="px-5 py-3">Revenue</th>
                      <th className="px-5 py-3">Avg / invoice</th>
                      <th className="px-5 py-3">Discounts</th>
                      <th className="px-5 py-3">Discount rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rankedServices.length ? rankedServices.map((row) => (
                      <tr key={`${row.appointmentTypeId}-${row.serviceName}`} className="align-top">
                        <td className="px-5 py-3">
                          <div className="font-medium text-slate-900">{row.serviceName}</div>
                        </td>
                        <td className="px-5 py-3 text-slate-600">{formatNumber(row.invoiceCount)}</td>
                        <td className="px-5 py-3 font-medium text-slate-900">{formatCurrency(row.revenue)}</td>
                        <td className="px-5 py-3 text-slate-600">{formatCurrency(row.averageRevenuePerInvoice)}</td>
                        <td className="px-5 py-3 text-slate-600">{formatCurrency(row.discounts)}</td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                            row.discountRate >= 12
                              ? 'bg-amber-50 text-amber-700'
                              : row.discountRate > 0
                                ? 'bg-slate-100 text-slate-600'
                                : 'bg-emerald-50 text-emerald-700'
                          }`}>
                            {formatPercent(row.discountRate)}
                          </span>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td className="px-5 py-5 text-slate-500" colSpan={6}>No service revenue for this range.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        ) : null}
      </main>
    </div>
  );
}

function MetricCard({
  icon,
  label,
  value,
  detail,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  detail: string;
  tone: 'blue' | 'emerald' | 'amber' | 'violet' | 'slate';
}) {
  const toneStyles = {
    blue: 'bg-sky-50 text-sky-700 ring-sky-100',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    amber: 'bg-amber-50 text-amber-700 ring-amber-100',
    violet: 'bg-violet-50 text-violet-700 ring-violet-100',
    slate: 'bg-slate-50 text-slate-600 ring-slate-100',
  } as const;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
          <p className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">{value}</p>
        </div>
        <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ring-1 ${toneStyles[tone]}`}>{icon}</span>
      </div>
      <p className="mt-2 text-xs font-medium text-slate-500">{detail}</p>
    </div>
  );
}

function InsightRow({ title, detail, tone }: { title: string; detail: string; tone: 'good' | 'watch' | 'neutral' }) {
  const toneStyles = {
    good: 'border-emerald-200 bg-emerald-50',
    watch: 'border-amber-200 bg-amber-50',
    neutral: 'border-slate-200 bg-slate-50',
  } as const;

  return (
    <div className={`rounded-xl border p-3 ${toneStyles[tone]}`}>
      <p className="text-sm font-semibold text-slate-950">{title}</p>
      <p className="mt-1 text-xs leading-5 text-slate-600">{detail}</p>
    </div>
  );
}
